import React, { useEffect, useRef } from 'react';

export default function DeleteItem({
  entry,
  clickHandle,
  onStartDeleteDrag,
  onEndDeleteDrag,
  startDragMaxOffset,
  startDragMinOffset,
  endDragMaxOffset,
  endDragMinOffset,
  deleteRange,
  trimDelete,
  fps,
  allowDeleteAdjustment,
  isSelected
}: {
  entry: [string, { bounds: [number, number] }];
  clickHandle: (e: React.MouseEvent<HTMLDivElement>) => void;
  selectedStartDelete: string | null;
  selectedEndDelete: string | null;
  onStartDeleteDrag: (
    e: React.MouseEvent<HTMLDivElement> | MouseEvent,
    id: string,
    maxOffset: number,
    minOffset: number
  ) => void;
  onEndDeleteDrag: (
    e: React.MouseEvent<HTMLDivElement> | MouseEvent,
    id: string,
    maxOffset: number,
    minOffset: number
  ) => void;
  startDragMaxOffset: number;
  startDragMinOffset: number;
  endDragMaxOffset: number;
  endDragMinOffset: number;
  deleteRange: number[];
  trimDelete: { bounds: number[] } | undefined;
  fps: number;
  allowDeleteAdjustment: boolean;
  isSelected: boolean;
}) {
  const startDragHandleRef = useRef<HTMLDivElement>(null);
  const endDragHandleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const startDragHandle = startDragHandleRef.current;
    const endDragHandle = endDragHandleRef.current;
    const preventActionPropagation = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    };
    const startDragHandleFunction = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onStartDeleteDrag(e, entry[0], startDragMaxOffset, -startDragMinOffset);
    };
    const endDragHandleFunction = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onEndDeleteDrag(e, entry[0], endDragMaxOffset, -endDragMinOffset);
    };
    if (startDragHandle) {
      startDragHandle.addEventListener('mousedown', startDragHandleFunction);
      startDragHandle.addEventListener('click', preventActionPropagation);
    }
    if (endDragHandle) {
      endDragHandle.addEventListener('mousedown', endDragHandleFunction);
      endDragHandle.addEventListener('click', preventActionPropagation);
    }
    return () => {
      if (startDragHandle) {
        startDragHandle.removeEventListener('mousedown', startDragHandleFunction);
        startDragHandle.removeEventListener('click', preventActionPropagation);
      }
      if (endDragHandle) {
        endDragHandle.removeEventListener('mousedown', endDragHandleFunction);
        endDragHandle.removeEventListener('click', preventActionPropagation);
      }
    };
  }, [
    startDragMaxOffset,
    startDragMinOffset,
    endDragMaxOffset,
    endDragMinOffset,
    onStartDeleteDrag,
    onEndDeleteDrag,
    entry
  ]);

  return (
    <div
      key={entry[0]}
      className="group pointer-events-auto relative"
      style={{
        position: 'absolute',
        width: `calc(${fps * (deleteRange[1] - deleteRange[0])}px * var(--timeline-zoom))`,
        left: `calc(${fps * (deleteRange[0] - (trimDelete ? trimDelete.bounds[0] : 0))}px * var(--timeline-zoom))`,
        top: '38px',
        bottom: '4px'
      }}
      onClick={clickHandle}
    >
      {allowDeleteAdjustment ? (
        <>
          <div
            className="absolute inset-y-0 left-0 z-10 flex w-4 max-w-[50%] cursor-ew-resize items-center justify-center opacity-0 group-hover:opacity-100"
            onMouseDown={e => onStartDeleteDrag(e, entry[0], startDragMaxOffset, -startDragMinOffset)}
            ref={startDragHandleRef}
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <div className="h-1/2 max-h-8 w-0.5 rounded bg-white"></div>
          </div>
          <div
            className="absolute inset-y-0 right-0 z-10 flex w-4 max-w-[50%] cursor-ew-resize items-center justify-center opacity-0 group-hover:opacity-100"
            onMouseDown={e => onEndDeleteDrag(e, entry[0], endDragMaxOffset, -endDragMinOffset)}
            ref={endDragHandleRef}
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <div className="h-1/2 max-h-8 w-0.5 rounded bg-white"></div>
          </div>
        </>
      ) : (
        <></>
      )}

      <div
        className="size-full rounded-sm border border-transparent bg-slate-400 opacity-[0.69] group-hover/delete:border-slate-600"
        data-testid="timeline-deleted-section"
      >
        {isSelected && (
          <div className="absolute inset-0 z-10 rounded-sm border border-transparent bg-deep-orange/35"></div>
        )}
      </div>
    </div>
  );
}
